import type { UploadDestination } from "./uploadJobContract";

/**
 * Routing is deliberately conservative. These are handoff signals, not a
 * promise about how long extraction will take; the backend can promote a
 * durable job later when it has a better estimate.
 */
export const BACKGROUND_HANDOFF_SIZE_BYTES = 25 * 1024 * 1024;
export const BACKGROUND_HANDOFF_BATCH_COUNT = 2;

export type UploadRouting = "inline" | "background_required";

export type UploadRoutingInput = {
  sizeBytes?: number | null;
  batchCount?: number;
  destination?: UploadDestination;
  /** Prefill surfaces need a candidate in the initiating flow. */
  foregroundRequired?: boolean;
};

export function classifyUploadRouting({
  sizeBytes,
  batchCount = 1,
  foregroundRequired = false,
}: UploadRoutingInput): UploadRouting {
  if (foregroundRequired) return "inline";
  if (batchCount >= BACKGROUND_HANDOFF_BATCH_COUNT) {
    return "background_required";
  }
  if (sizeBytes != null && sizeBytes >= BACKGROUND_HANDOFF_SIZE_BYTES) {
    return "background_required";
  }
  return "inline";
}

export function shouldCreateDurableUploadJob(input: UploadRoutingInput) {
  return classifyUploadRouting(input) === "background_required";
}

export function destinationForUploadContext({
  slotId,
  category,
}: {
  slotId?: string | null;
  category?: string | null;
}): UploadDestination {
  const context = `${slotId ?? ""} ${category ?? ""}`.toLowerCase();
  if (context.includes("lab") || context.includes("blood")) {
    return "lab_result";
  }
  if (
    context.includes("image") ||
    context.includes("ultrasound") ||
    context.includes("scan")
  ) {
    return "imaging_result";
  }
  if (
    context.includes("treatment") ||
    context.includes("medication") ||
    context.includes("prescription")
  ) {
    return "medication";
  }
  if (context.includes("appointment")) return "appointment";
  if (context.includes("insurance") || context.includes("coverage")) {
    return "insurance";
  }
  if (context.includes("specimen")) return "specimen";
  return "document";
}
